import { AHInput, AHText, Box } from "arkh";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { useNavigationHeader } from "../../../common/navigationHeaderOptions";
import { UserLoggedInNavigatorScreenProps } from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { useLazyLoadQuery, graphql, useMutation } from "react-relay";
import { OrgWorkspaceAdminQuery } from "./__generated__/OrgWorkspaceAdminQuery.graphql";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { OrgWorkspaceAdminMutation } from "./__generated__/OrgWorkspaceAdminMutation.graphql";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { WorkspaceSelector } from "../WorkspaceSelector/WorkspaceSelector";
import { useAlert } from "@/src/common/Alert";

function OrgWorkspaceAdminC(
  props: UserLoggedInNavigatorScreenProps<"OrgWorkspaceAdmin">
) {
  const data = useLazyLoadQuery<OrgWorkspaceAdminQuery>(
    graphql`
      query OrgWorkspaceAdminQuery($workspaceId: ID!) {
        workspace(id: $workspaceId) {
          id
          name
          parentId
        }
        viewer {
          workspaces {
            parentId
            id
            ...WorkspaceSelector_workspaces
          }
        }
      }
    `,
    {
      workspaceId: props.route.params.workspaceId,
    }
  );

  const [setNameMutation, inFlight] = useMutation<OrgWorkspaceAdminMutation>(
    graphql`
      mutation OrgWorkspaceAdminMutation(
        $workspaceId: ID!
        $name: String
        $parentWorkspaceId: ID
      ) {
        workspace(id: $workspaceId) {
          update(setName: $name, setParentWorkspaceId: $parentWorkspaceId) {
            id
            name
            parentId
          }
        }
      }
    `
  );

  const [deleteMutation,] =
    useMutation<OrgWorkspaceAdminMutation>(graphql`
      mutation OrgWorkspaceAdminDeleteMutation($workspaceId: ID!) {
        workspace(id: $workspaceId) {
          delete {
            id
          }
        }
      }
    `);

  const intl_tokens = useIntlTokens();
  const [name, setName] = useState<null | string>(null);
  const [parentWorkspaceId, setParentWorkspaceId] = useState<null | string>(
    null
  );

  const nameHasChanged = name ? name !== data.workspace.name : false;
  const parentWorkspaceIdHasChanged = parentWorkspaceId
    ? parentWorkspaceId !== data.workspace.parentId
    : false;

  const Alert = useAlert();

  const onDelete = () => {
    Alert.alert("Delete", "Are you sure you want to delete this workspace?", [
      {
        text: intl_tokens.Cancel,
        style: "cancel",
      },
      {
        text: intl_tokens.Delete,
        style: "destructive",
        onPress: () => {
          deleteMutation({
            variables: {
              workspaceId: props.route.params.workspaceId,
            },
          });
        },
      },
    ]);
  };

  useNavigationHeader({
    type: "SAVE",

    onCancel: props.navigation.goBack,
    title: intl_tokens.WorkspaceAdmin,
    saveLoading: inFlight,
    disabled: !(nameHasChanged || parentWorkspaceIdHasChanged),
    more: [
      {
        label: intl_tokens.Delete,
        onPress: onDelete,
        type: "button",
        color: "negative",
        icon: "trash",
      },
    ],
    onSave: () => {
      setNameMutation({
        variables: {
          workspaceId: props.route.params.workspaceId,
          name,
          parentWorkspaceId,
        },
      });
    },
  });

  return (
    <Box space="medium">
      <Box>
        <AHText spaceBottom="small">
          <FormattedMessage
            defaultMessage="Workspace name"
            description="Workspace Admin Name Label"
          />
        </AHText>
        <AHInput
          defaultStyle
          value={name ?? data.workspace.name}
          onChangeText={(text) => setName(text)}
        />
      </Box>
      <Box spaceTop="xlarge">
        <AHText spaceBottom="small">
          <FormattedMessage
            defaultMessage="Parent workspace"
            description="Workspace Admin Parent Workspace Label"
          />
        </AHText>
        {data.workspace.parentId === null ? null : (
          <Box spaceHorizontal="xsmall">
            <WorkspaceSelector
              workspaces={data.viewer?.workspaces ?? []}
              onSelectWorkspace={(id) => {
                setParentWorkspaceId(id);
              }}
              workspaceId={parentWorkspaceId ?? data.workspace.parentId}
              disabledIds={getDisabledIds(
                props.route.params.workspaceId,
                data.viewer?.workspaces ?? []
              )}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

function getDisabledIds(
  workspaceId: string,
  workspaces: readonly { parentId: string | null; id: string }[]
) {
  const childMap: Record<string, string[]> = {};
  workspaces.forEach(({ id, parentId }) => {
    if (parentId !== null) {
      if (!childMap[parentId]) {
        childMap[parentId] = [];
      }
      childMap[parentId].push(id);
    }
  });

  const disabledIds = new Set<string>();
  function dfs(currentId: string) {
    disabledIds.add(currentId);
    const children = childMap[currentId];
    if (children) {
      children.forEach((childId) => {
        if (!disabledIds.has(childId)) {
          dfs(childId);
        }
      });
    }
  }

  dfs(workspaceId);

  return Array.from(disabledIds);
}

export const OrgWorkspaceAdmin = withWebModalStack(OrgWorkspaceAdminC);
